import React, { useEffect, useState, useRef, useMemo } from 'react';
import axios from 'axios';
import echo from '../../echo';
import AnimationRevealPage from 'helpers/AnimationRevealPage';
import LightAdmin from 'components/headers/lightAdmin';
import './adminChat.css';
import Footerr from 'components/footers/Footer';
import { useTranslation } from 'react-i18next';

const AdminChat = () => {
  const { t } = useTranslation();
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [error, setError] = useState(null);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [unreadSessions, setUnreadSessions] = useState(new Set());
  const messagesEndRef = useRef(null);
  // Stable axios instance to avoid infinite re-render loops caused by changing dependencies
  const api = useMemo(() => axios.create({ baseURL: process.env.REACT_APP_API_URL }), []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Function to fetch sessions
  const fetchSessions = useRef(null);
  
  fetchSessions.current = async () => {
    setLoadingSessions(true);
    try {
      const res = await api.get('/admin/sessions');
      const newSessions = res.data;
      
      // Check each session for unread client messages
      for (const session of newSessions) {
        try {
          const messagesRes = await api.get(`/admin/messages/${session.id}`);
          const sessionMessages = messagesRes.data;
          
          // Check if last message is from client
          if (sessionMessages.length > 0) {
            const lastMessage = sessionMessages[sessionMessages.length - 1];
            if ((lastMessage.sender_type === 'client' || lastMessage.sender_type === 'Client') && 
                session.id !== currentSession?.id) {
              setUnreadSessions(prev => new Set([...prev, session.id]));
            } else if (lastMessage.sender_type === 'admin' || lastMessage.sender_type === 'Admin') {
              // Remove from unread if admin sent last message
              setUnreadSessions(prev => {
                const newSet = new Set(prev);
                newSet.delete(session.id);
                return newSet;
              });
            }
          }
        } catch (err) {
          console.error(`Error fetching messages for session ${session.id}:`, err);
        }
      }
      
      setSessions(newSessions);
      setError(null);
    } catch (err) {
      console.error('Error fetching sessions:', err);
      setError(t('adminChat.failedLoadSessions') + ' ' + (err.message || '')); 
    } finally {
      setLoadingSessions(false);
    }
  };

  // Fetch sessions on mount
  useEffect(() => {
    fetchSessions.current();
  }, []);

  // Poll for new sessions every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchSessions.current();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Listen to global chat channel for all messages
  useEffect(() => {
    const baseChannel = 'chat';
    let globalChannel = null;

    try {
      globalChannel = echo.channel(baseChannel);
      
      globalChannel.listen('PrivateMessageSent', (e) => {
        console.log('[Echo Global] Message received:', e);
        
        // If message is from client and not in current session, mark as unread
        if ((e.sender_type === 'client' || e.sender_type === 'Client') && 
            e.session_id && e.session_id !== currentSession?.id) {
          setUnreadSessions(prev => new Set([...prev, e.session_id]));
          // Refresh sessions list to update
          fetchSessions.current();
        }
      });
      
      console.log('[Echo] Subscribed to global chat channel');
    } catch (err) {
      console.error('[Echo] Failed to subscribe to global channel:', err);
    }

    return () => {
      if (globalChannel) {
        try {
          echo.leave(baseChannel);
          console.log('[Echo] Left global chat channel');
        } catch (err) {
          console.error('[Echo] Error leaving global channel:', err);
        }
      }
    };
  }, [currentSession]);

// Fetch messages when session changes
useEffect(() => {
  if (!currentSession) return;
  const fetchMessages = async () => {
    setLoadingMessages(true);
    try {
      const res = await api.get(`/admin/messages/${currentSession.id}`);
      setMessages(res.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError(t('adminChat.failedLoadMessages') + ' ' + (err.message || ''));
    } finally {
      setLoadingMessages(false);
    }
  };
  fetchMessages();
}, [currentSession, api, t]);

// Real-time subscription with diagnostics & fallbacks
useEffect(() => {
  if (!currentSession) return;
  const baseChannel = 'chat';
  const possibleChannels = [
    baseChannel, // public channel
    `private-${baseChannel}`, // automatic Laravel private naming when using echo.channel may require echo.private
    `${baseChannel}.${currentSession.id}`, // session-scoped channel
    `private-${baseChannel}.${currentSession.id}`,
  ];

  let activeChannelRef = { name: null, type: null };
  let subscribed = false;

  const attachListeners = (channelObj, channelName, type) => {
    activeChannelRef = { name: channelName, type };
    channelObj.listen('PrivateMessageSent', (e) => {
      console.log('[Echo] Event PrivateMessageSent received on', channelName, 'payload:', e);
      const incoming = e.message ? {
        id: e.id || Date.now(),
        sender_type: e.sender_type || e.sender || 'admin',
        message: e.message || e.text || JSON.stringify(e),
        created_at: e.created_at || new Date().toISOString(),
      } : e; // fallback if structure matches what we expect already
      setMessages(prev => [...prev, incoming]);
      
      // If message is from client and not in current session, mark as unread
      if ((incoming.sender_type === 'client' || incoming.sender_type === 'Client') && 
          e.session_id && e.session_id !== currentSession?.id) {
        setUnreadSessions(prev => new Set([...prev, e.session_id]));
      }
    });
  };

  console.log('[Echo] Attempting subscription candidates:', possibleChannels);

  try {
    for (const candidate of possibleChannels) {
      if (subscribed) break;
      try {
        // Decide whether to use private channel helper
        let channelObj;
        if (candidate.startsWith('private-')) {
          const stripped = candidate.replace(/^private-/, '');
          channelObj = echo.private(stripped);
          attachListeners(channelObj, candidate, 'private');
        } else if (candidate.startsWith(baseChannel) && candidate !== baseChannel) {
          // Likely presence or private pattern; try private first then public
          try {
            channelObj = echo.private(candidate);
            attachListeners(channelObj, candidate, 'private');
          } catch (e) {
            channelObj = echo.channel(candidate);
            attachListeners(channelObj, candidate, 'public');
          }
        } else {
          channelObj = echo.channel(candidate);
          attachListeners(channelObj, candidate, 'public');
        }
        subscribed = true;
        console.log('[Echo] Subscribed to', activeChannelRef);
      } catch (innerErr) {
        console.warn('[Echo] Subscription failed for', candidate, innerErr);
      }
    }
  } catch (err) {
    console.error('[Echo] Unexpected subscription error:', err);
    setError('Real-time subscription failed. ' + (err.message || ''));
  }

  // Connection diagnostics
  if (echo?.connector?.pusher?.connection) {
    const connection = echo.connector.pusher.connection;
    connection.bind('state_change', (states) => {
      console.log('[Echo] Connection state_change', states.previous, '->', states.current);
    });
    connection.bind('error', (err) => {
      console.error('[Echo] Connection error', err);
      setError('Real-time connection error. Verify Pusher credentials & backend broadcasting config.');
    });
  }

  return () => {
    if (activeChannelRef.name) {
      console.log('[Echo] Leaving channel', activeChannelRef);
      try { echo.leave(activeChannelRef.name); } catch (_) {}
    }
  };
}, [currentSession]);

// Polling fallback every 10s if realtime not established
useEffect(() => {
  if (!currentSession) return;
  const interval = setInterval(async () => {
    try {
      const res = await api.get(`/admin/messages/${currentSession.id}`);
      // Merge new messages only
      setMessages(prev => {
        const existingIds = new Set(prev.map(m => m.id));
        const incoming = res.data.filter(m => !existingIds.has(m.id));
        return incoming.length ? [...prev, ...incoming] : prev;
      });
    } catch (e) {
      // Silent polling error
    }
  }, 10000); // 10 seconds
  return () => clearInterval(interval);
}, [currentSession, api]);

 const sendMessage = () => {
  if (!newMsg.trim() || !currentSession || isSending) return;

  setIsSending(true);
  const messageToSend = newMsg;
  setNewMsg(''); // Clear input immediately
  
  api.post('/admin/send', { session_id: currentSession.id, message: messageToSend })
    .then(() => {
      setError(null);
      // Mark current session as read since admin replied
      setUnreadSessions(prev => {
        const newSet = new Set(prev);
        newSet.delete(currentSession.id);
        return newSet;
      });
      // Message will be added via real-time listener (Echo)
    })
    .catch(err => {
      console.error('Error sending message:', err);
      setError('Failed to send message. ' + (err.message || ''));
      // Restore message on error so user can retry
      setNewMsg(messageToSend);
    })
    .finally(() => {
      setIsSending(false);
    });
};

  const renderInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((n) => n[0].toUpperCase())
      .join('');
  };

  const isAdminMessage = (m) => m.sender_type === 'admin' || m.sender_type === 'Admin';

  return (
    <AnimationRevealPage>
      <LightAdmin />
      <div className="admin-chat-page">
        <div className="admin-chat-container">
          <aside className="chat-sidebar">
            <div className="chat-sidebar-header">{t('adminChat.conversations')}</div>
            <div className="chat-session-list">
              {sessions.map((s) => (
                <div
                  key={s.id}
                  onClick={() => {
                    setCurrentSession(s);
                    // Mark as read when clicked
                    setUnreadSessions(prev => {
                      const newSet = new Set(prev);
                      newSet.delete(s.id);
                      return newSet;
                    });
                  }}
                  className={
                    'chat-session-item' +
                    (currentSession && currentSession.id === s.id ? ' active' : '')
                  }
                >
                  <div className="chat-session-avatar">
                    {renderInitials(s.client_name || `U${s.user_id}`)}
                  </div>
                  <div className="chat-session-info">
                    <span className="chat-session-name">
                      {s.client_name || `${t('adminChat.user')} ${s.user_id}`}
                    </span>
                    <span className="chat-session-subtitle">{t('adminChat.clickToOpen')}</span>
                  </div>
                  {unreadSessions.has(s.id) && (
                    <div className="chat-notification-badge">
                      <span>•</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </aside>

          <section className="chat-main">
            {error && (
              <div className="chat-error-banner">
                {error} {loadingSessions || loadingMessages ? t('adminChat.retrying') : ''}
              </div>
            )}
            {currentSession ? (
              <>
                <header className="chat-main-header">
                  <div>
                    <div className="chat-main-title">
                      {currentSession.client_name || `${t('adminChat.user')} ${currentSession.user_id}`}
                    </div>
                    <div className="chat-main-subtitle">{t('adminChat.lastSeenRecently')}</div>
                  </div>
                </header>

                <div className="chat-messages">
                  {loadingMessages && (
                    <div className="chat-loading">{t('adminChat.loadingMessages')}</div>
                  )}
                  {messages.map((m, i) => (
                    <div
                      key={i}
                      className={
                        'chat-message-row ' + (isAdminMessage(m) ? 'admin' : 'client')
                      }
                    >
                      <div
                        className={
                          'chat-message-bubble ' +
                          (isAdminMessage(m) ? 'admin' : 'client')
                        }
                      >
                        {m.message}
                        {m.created_at && (
                          <div className="chat-message-meta">
                            {new Date(m.created_at).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                <div className="chat-input-bar">
                  <input
                    className="chat-input"
                    value={newMsg}
                    onChange={(e) => setNewMsg(e.target.value)}
                    placeholder={t('adminChat.typePlaceholder')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') sendMessage();
                    }}
                    disabled={isSending}
                  />
                  <button 
                    className="chat-send-btn" 
                    onClick={sendMessage}
                    disabled={isSending}
                  >
                    {isSending ? t('adminChat.sending') : '➤'}
                  </button>
                </div>
              </>
            ) : (
              <div className="chat-empty-state">{t('adminChat.selectConversation')}</div>
            )}
          </section>
        </div>
      </div>
                  <Footerr />
      
    </AnimationRevealPage>
  );
};

export default AdminChat;
